import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Waves, BookOpen, Compass, CircleDot, Droplets } from "lucide-react";
import { Footer } from "@/components/site/Footer";
import { FloatingWhatsApp } from "@/components/site/FloatingWhatsApp";
import { CONTACT } from "@/lib/scroll";

const EASE = [0.16, 1, 0.3, 1];

const Section = ({ title, children }) => (
  <motion.section
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.9, ease: EASE }}
    className="mb-16 md:mb-20"
  >
    <h2 className="font-serif text-3xl md:text-4xl text-pawc-deep mb-6">{title}</h2>
    <div className="space-y-5 text-pawc-slate text-base md:text-lg leading-[1.85]">
      {children}
    </div>
  </motion.section>
);

const MAPPINGS = [
  {
    icon: BookOpen,
    title: "Narrative psychology",
    text: "Treats a person's story not as a fixed record but as something continually retold and reshaped, closer to a current than a monument. The work is not to correct the story, but to help someone find a version of it they can actually live inside.",
  },
  {
    icon: Compass,
    title: "Yalom's existential psychotherapy",
    text: "Returns often to the idea that a great deal of suffering comes from resisting the fixed facts of existence: mortality, freedom, isolation, the search for meaning. His therapeutic stance carries a strong flavour of yielding to what is true about being alive, rather than arguing with it.",
  },
  {
    icon: CircleDot,
    title: "Gestalt therapy",
    text: "Used here especially with younger clients through play, it is built on the whole shape of a person's present experience (Gestalt literally means form or shape in German): attending to what is alive right now rather than analysing a fixed past. Form again, but a living, moving one.",
  },
  {
    icon: Droplets,
    title: "Alan Watts",
    text: "Spent much of his life translating Daoist and Zen ideas for a Western audience, arguing that much psychological suffering comes from trying to grip what was only ever meant to flow: a feeling, an identity, a moment. The aim is not to stop the water, but to stop fighting it.",
  },
];

export default function StoryPage() {
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="story-page" className="bg-pawc-sand">
      <section className="pt-28 pb-16 md:pt-36 md:pb-20 border-b border-pawc-teal/10">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Link
            data-testid="story-back-link"
            to="/"
            className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-pawc-slate hover:text-pawc-teal transition-colors duration-300 mb-12"
          >
            <ArrowLeft size={14} /> Back to the site
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE }}
          >
            <img
              src="/images/logo-transparent.png"
              alt="Yielding: Flow and Form logo, a current of water curling around a smooth stone with leaves"
              className="w-52 md:w-64 mx-auto mb-10"
            />
            <p className="text-xs tracking-[0.3em] uppercase text-pawc-teal mb-4">
              The philosophy behind the name
            </p>
            <h1 className="font-serif text-4xl md:text-6xl text-pawc-deep tracking-tight">
              {CONTACT.brand}: {CONTACT.brandSub}
            </h1>
          </motion.div>
        </div>
      </section>

      <div className="max-w-2xl mx-auto px-6 py-16 md:py-24">
        <Section title='Why "Yielding"'>
          <p>
            In the Tao Te Ching, Laozi returns again and again to water as a model
            for how change actually happens. Water does not force its way through
            stone. It wears stone down simply by continuing to move, taking the
            shape of whatever it meets, finding the lowest and softest path rather
            than the hardest one. Laozi calls this the highest form of goodness,
            precisely because water helps everything without competing with it.
          </p>
          <p>
            This is the root of <em>wu wei</em>, often translated as effortless
            action. It does not mean passivity or giving up. It means acting in a
            way that works with the nature of a situation instead of fighting it.
            The person who yields is not weak; they are the one still standing when
            the rigid thing has snapped. Softness, in this tradition, is a form of
            strength, arguably the more durable one.
          </p>
          <p>
            That is the idea behind the name Yielding. In therapy, so much of what
            brings people to the room is a kind of fighting: fighting a feeling, a
            memory, a diagnosis, a version of themselves they have been told they
            should be. Yielding here means something quite specific. Not
            resignation, but the willingness to stop bracing against what is
            actually true, so that real movement becomes possible. You cannot be
            carried by a current you are still trying to stand still against.
          </p>
        </Section>

        <Section title='Why "Flow & Form"'>
          <p>
            Water only becomes visible, only does its work in the world, because it
            takes a shape: a riverbed, a container, a coastline. Flow without any
            form at all is not healing, it is just dispersal. And form without
            flow is not structure, it is rigidity.
          </p>
          <p>
            <span className="text-pawc-deep font-medium">Flow</span> is the Daoist
            half of this practice: the felt sense of moving with a client's own
            pace and material rather than imposing a fixed programme on them; the
            trust that a person's story, followed honestly, tends toward its own
            kind of order.
          </p>
          <p>
            <span className="text-pawc-deep font-medium">Form</span> is the
            psychological half: the actual scaffolding of the work. The safety of
            the therapeutic relationship, the techniques (Gestalt play work, CBT,
            structured assessment) that give a session its shape, and the
            professional and ethical container of HPCSA registration, formal
            training and supervision that makes it safe to be vulnerable inside.
          </p>
          <p>
            Held together, flow and form describe how change happens in this room:
            enough structure that a person feels safe, enough space that they are
            free to move.
          </p>
        </Section>

        <motion.section
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="mb-16 md:mb-20"
        >
          <h2 className="font-serif text-3xl md:text-4xl text-pawc-deep mb-4">
            Where this meets psychology
          </h2>
          <p className="text-pawc-slate text-base md:text-lg leading-[1.85] mb-8">
            This is not just a Daoist metaphor placed beside a therapy practice. It
            maps onto real traditions in the field:
          </p>
          <div className="space-y-5">
            {MAPPINGS.map((m) => (
              <div
                key={m.title}
                data-testid={`story-mapping-${m.title.split(" ")[0].toLowerCase()}`}
                className="rounded-2xl bg-pawc-paper border border-pawc-teal/10 p-6 md:p-7 flex gap-5"
              >
                <span className="shrink-0 mt-1 text-pawc-terra">
                  <m.icon size={20} strokeWidth={1.5} />
                </span>
                <div>
                  <h3 className="font-serif text-xl text-pawc-deep mb-2">{m.title}</h3>
                  <p className="text-sm md:text-base text-pawc-slate leading-relaxed">{m.text}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.section>

        <motion.blockquote
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          data-testid="story-one-liner"
          className="rounded-3xl bg-pawc-deep text-pawc-sand p-10 md:p-14 text-center"
        >
          <Waves size={22} strokeWidth={1.5} className="mx-auto mb-6 text-pawc-terra" />
          <p className="font-serif italic text-2xl md:text-3xl leading-relaxed">
            "Yielding is the willingness to stop resisting what is true. Flow and
            form are how that willingness becomes something you can actually stand
            on."
          </p>
        </motion.blockquote>
      </div>

      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}
